import { t } from 'i18next';
import { useState, useCallback } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import { calculatePermissions } from 'hooks/useWorkspacePermissions/utils';
import { RoleAssignment } from 'modules/CreateWorkspaceModal/types';
import logger from 'utils/logging/faro';

import { isEmailFormat, isMatrixIdFormat } from './WorkspaceInviteForm/utils';
import {
  applyInviteeRoleAssignments,
  canSendWorkspaceRolesEvent,
  captureInviteeAssignmentSnapshots,
  getLiveWorkspaceRolesState,
  revertFailedInviteeAssignments,
  sendWorkspaceRolesState,
} from './workspaceInviteRoles';

const useWorkspaceInvite = () => {
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const pushNoPermissionToast = useNoPermissionToast();
  const [isLoading, setIsLoading] = useState(false);

  const inviteUsers = useCallback(
    async (workspaceId: string, invitees: string[], roleId: string | null) => {
      const { invitePeople, adjustRoles } = calculatePermissions(matrixClient, workspaceId);
      if (!invitePeople) return pushNoPermissionToast();

      const matrixIds = invitees.filter(isMatrixIdFormat);
      const emails = invitees.filter(isEmailFormat);

      if (!matrixIds.length && !emails.length) return;

      setIsLoading(true);

      const failedInvites: string[] = [];
      const shouldAssignRoles = adjustRoles && !!roleId;
      const allInvitees = [...matrixIds, ...emails];
      let assignmentSnapshots = new Map<string, RoleAssignment | null>();

      try {
        if (shouldAssignRoles) {
          const currentUserId = matrixClient.getUserId();
          const workspaceRoom = matrixClient.getRoom(workspaceId);

          if (!workspaceRoom || !currentUserId) {
            pushToast({
              variant: 'warning',
              title: t('workspaces_role_update_fail'),
              description: t('try_again_and_reload'),
            });
            return;
          }

          const liveState = getLiveWorkspaceRolesState(matrixClient, workspaceId);
          if (!liveState) {
            pushToast({
              variant: 'warning',
              title: t('workspaces_role_update_fail'),
              description: t('try_again_and_reload'),
            });
            return;
          }

          const roleExists = liveState.roles.some(({ id }) => id === roleId);
          if (!roleExists) {
            logger.error('Failed to assign workspace roles before invite: role not found', {
              workspaceId,
              roleId,
            });
            pushToast({
              variant: 'warning',
              title: t('workspaces_role_update_fail'),
              description: t('try_again_and_reload'),
            });
            return;
          }

          if (!calculatePermissions(matrixClient, workspaceId).adjustRoles) {
            pushToast({
              variant: 'info',
              title: t('workspaces.no_permission'),
              description: t('workspaces.no_permission_description'),
            });
            return;
          }

          if (!canSendWorkspaceRolesEvent(workspaceRoom, currentUserId)) {
            pushToast({
              variant: 'info',
              title: t('workspaces.no_permission'),
              description: t('workspaces.no_permission_description'),
            });
            return;
          }

          assignmentSnapshots = captureInviteeAssignmentSnapshots(liveState.assignments, allInvitees);

          try {
            const updatedAssignments = applyInviteeRoleAssignments(
              liveState.assignments,
              allInvitees,
              roleId,
              currentUserId
            );

            await sendWorkspaceRolesState(matrixClient, workspaceId, liveState.roles, updatedAssignments);
          } catch (error) {
            logger.error('Failed to batch-assign workspace roles before invite', {
              error,
              workspaceId,
              inviteeCount: allInvitees.length,
              roleId,
            });
            pushToast({
              variant: 'warning',
              title: t('workspaces_role_update_fail'),
              description: t('try_again_and_reload'),
            });
            return;
          }
        }

        for (const matrixId of matrixIds) {
          try {
            await matrixClient.invite(workspaceId, matrixId);
            logger.info(`Successfully invited ${matrixId} to workspace ${workspaceId}`);
          } catch (error) {
            logger.error(`Failed to invite ${matrixId} to workspace ${workspaceId}:`, error);
            failedInvites.push(matrixId);
          }
        }

        for (const email of emails) {
          try {
            await matrixClient.inviteByEmail(workspaceId, email);
            logger.info(`Successfully invited ${email} to workspace ${workspaceId}`);
          } catch (error) {
            logger.error(`Failed to invite ${email} to workspace ${workspaceId}:`, error);
            failedInvites.push(email);
          }
        }

        if (shouldAssignRoles && failedInvites.length > 0) {
          const currentUserId = matrixClient.getUserId();
          const workspaceRoom = matrixClient.getRoom(workspaceId);

          if (workspaceRoom && currentUserId && canSendWorkspaceRolesEvent(workspaceRoom, currentUserId)) {
            const liveState = getLiveWorkspaceRolesState(matrixClient, workspaceId);

            if (liveState) {
              try {
                const revertedAssignments = revertFailedInviteeAssignments(
                  liveState.assignments,
                  failedInvites,
                  assignmentSnapshots
                );

                await sendWorkspaceRolesState(matrixClient, workspaceId, liveState.roles, revertedAssignments);
              } catch (error) {
                logger.error('Failed to revert workspace role assignments after failed invites', {
                  error,
                  workspaceId,
                  failedInvitees: failedInvites,
                });
                pushToast({
                  variant: 'warning',
                  title: t('workspaces_role_update_fail'),
                  description: t('try_again_and_reload'),
                });
              }
            } else {
              logger.error('Failed to revert workspace role assignments: roles event missing', {
                workspaceId,
                failedInvitees: failedInvites,
              });
            }
          }
        }

        if (failedInvites.length) {
          pushToast({
            variant: 'warning',
            title: t('workspaces.participants_not_invited'),
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [matrixClient, pushNoPermissionToast, pushToast]
  );

  return { inviteUsers, isLoading };
};

export default useWorkspaceInvite;
